// 🌿 API Craft Back Office - Main App Component
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import Dashboard from './pages/Dashboard';
import ProfilePage from './pages/ProfilePage';
import ModelsPage from './pages/ModelsPage';
import CreateModelPage from './pages/CreateModelPage';
import AiChatPage from './pages/AiChatPage';
import ApiDocsPage from './pages/ApiDocsPage';
import ModelCrudPage from './pages/ModelCrudPage';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-mint-50 via-sage-50 to-emerald-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto bg-gradient-to-r from-mint-400 to-emerald-500 rounded-2xl flex items-center justify-center shadow-strong mb-4 animation-bounce-gentle">
            <span className="text-2xl text-white">🌿</span>
          </div>
          <h2 className="text-xl font-semibold text-forest-700 mb-2">Loading API Craft...</h2>
          <p className="text-sage-600">Please wait while we authenticate you</p>
          <div className="mt-4">
            <svg className="animate-spin h-8 w-8 text-mint-500 mx-auto" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        </div>
      </div>
    );
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Public Route Component (redirect to dashboard if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-mint-50 via-sage-50 to-emerald-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto bg-gradient-to-r from-mint-400 to-emerald-500 rounded-2xl flex items-center justify-center shadow-strong mb-4 animation-bounce-gentle">
            <span className="text-2xl text-white">🌿</span>
          </div>
          <h2 className="text-xl font-semibold text-forest-700 mb-2">Loading API Craft...</h2>
          <p className="text-sage-600">Please wait while we check your authentication</p>
          <div className="mt-4">
            <svg className="animate-spin h-8 w-8 text-mint-500 mx-auto" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        </div>
      </div>
    );
  }
  
  return !isAuthenticated ? <>{children}</> : <Navigate to="/" replace />;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <RegisterPage />
                </PublicRoute>
              }
            />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/models"
              element={
                <ProtectedRoute>
                  <ModelsPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/models/create"
              element={
                <ProtectedRoute>
                  <CreateModelPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/models/:modelName"
              element={
                <ProtectedRoute>
                  <ModelCrudPage />
                </ProtectedRoute>
              }
            />
            
                      <Route
            path="/ai-chat"
            element={
              <ProtectedRoute>
                <AiChatPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/api-docs"
            element={
              <ProtectedRoute>
                <ApiDocsPage />
              </ProtectedRoute>
            }
          />

            {/* Catch all route - redirect to dashboard */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
};

export default App;
